import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { ArrowRight, X } from "lucide-react";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

function embedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

export function GalleryCarousel() {
  const [images, setImages] = useState([]);
  useEffect(() => { api.get("/gallery").then((r) => setImages(r.data)).catch(() => {}); }, []);
  if (!images.length) return null;
  return (
    <section className="border-t border-[var(--line)]" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 lg:py-24">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--accent)]">In the room</span>
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tighter mt-2 mb-8">Life at Get Skilled</h2>
        <Carousel opts={{ loop: true, align: "start" }} className="w-full">
          <CarouselContent className="-ml-4">
            {images.map((img) => (
              <CarouselItem key={img.id} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                <div className="relative group border border-[var(--line)] overflow-hidden aspect-[4/3]">
                  {img.link_url ? (
                    <a href={img.link_url} target="_blank" rel="noopener noreferrer">
                      <img src={img.image_url} alt={img.caption || ""} className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500" />
                    </a>
                  ) : (
                    <img src={img.image_url} alt={img.caption || ""} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" />
                  )}
                  {img.caption && <div className="absolute bottom-0 left-0 right-0 bg-[var(--ink)]/80 text-white text-sm px-4 py-2 translate-y-full group-hover:translate-y-0 transition-transform">{img.caption}</div>}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="rounded-none -left-3 hidden sm:flex" />
          <CarouselNext className="rounded-none -right-3 hidden sm:flex" />
        </Carousel>
      </div>
    </section>
  );
}

export function LatestNews() {
  const [news, setNews] = useState([]);
  useEffect(() => { api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {}); }, []);
  if (!news.length) return null;
  return (
    <section className="border-t border-[var(--line)] bg-white" data-testid="latest-news-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 lg:py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--accent)]">Latest news</span>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tighter mt-2">What's happening</h2>
          </div>
          <Link to="/news" className="text-sm font-semibold inline-flex items-center gap-1.5 hover:text-[var(--accent)]" data-testid="news-view-all">All news <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 border-l border-t border-[var(--line)]">
          {news.map((n) => (
            <Link key={n.id} to={`/news/${n.slug}`} className="group border-r border-b border-[var(--line)] flex flex-col hover:bg-[var(--alt)] transition-colors" data-testid={`news-card-${n.slug}`}>
              {n.cover_url
                ? <div className="aspect-video overflow-hidden"><img src={n.cover_url} alt={n.title} className="w-full h-full object-cover transition-all duration-500" /></div>
                : <div className="aspect-video bg-[var(--alt)]" />}
              <div className="p-6 flex flex-col flex-1">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ink-2)]">{fmtDate(n.published_at)}</span>
                <h3 className="font-display font-bold text-xl tracking-tight mt-2 mb-2 leading-snug">{n.title}</h3>
                <p className="text-sm text-[var(--ink-2)] line-clamp-3">{n.excerpt}</p>
                <span className="mt-4 text-sm font-semibold text-[var(--accent)] inline-flex items-center gap-1.5">Read article <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function LearnerShowcase() {
  const [items, setItems] = useState([]);
  const [zoom, setZoom] = useState(null);
  useEffect(() => { api.get("/gallery").then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <section className="border-t border-[var(--line)] bg-[var(--alt)] grain" data-testid="learner-showcase-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 lg:py-24">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--accent)]">Success stories</span>
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tighter mt-2 mb-10">What our learners say</h2>
        <Carousel opts={{ loop: true, align: "start" }} className="w-full">
          <CarouselContent className="-ml-4">
            {items.map((img) => {
              const yt = img.video_url && img.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
              const vm = img.video_url && img.video_url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
              const embed = yt ? `https://www.youtube.com/embed/${yt[1]}` : vm ? `https://player.vimeo.com/video/${vm[1]}` : null;
              const isUploadedVideo = img.video_url && !embed;
              const Card = (
                <div className="relative group border border-[var(--line)] overflow-hidden aspect-[4/3] bg-[var(--ink)] cursor-pointer">
                  {embed
                    ? <iframe src={embed} title={img.caption || "Video"} className="w-full h-full" allow="accelerometer; encrypted-media" allowFullScreen />
                    : isUploadedVideo
                      ? <video src={img.video_url} controls className="w-full h-full object-cover bg-black" />
                      : <img src={img.image_url} alt={img.caption || "Learner success"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  {img.caption && <div className="absolute bottom-0 left-0 right-0 bg-[var(--ink)]/85 text-white text-sm px-4 py-2.5 translate-y-full group-hover:translate-y-0 transition-transform pointer-events-none">{img.caption}</div>}
                </div>
              );
              const isVideo = !!img.video_url;
              return (
                <CarouselItem key={img.id} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/4" data-testid={`showcase-item-${img.id}`}>
                  {img.link_url
                    ? <a href={img.link_url} target="_blank" rel="noopener noreferrer">{Card}</a>
                    : isVideo
                      ? Card
                      : <div onClick={() => setZoom(img)} role="button" data-testid={`showcase-zoom-${img.id}`}>{Card}</div>}
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious className="rounded-none -left-3 hidden sm:flex" data-testid="showcase-prev" />
          <CarouselNext className="rounded-none -right-3 hidden sm:flex" data-testid="showcase-next" />
        </Carousel>
      </div>
      {zoom && (
        <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4" onClick={() => setZoom(null)} data-testid="showcase-lightbox">
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setZoom(null)} data-testid="showcase-lightbox-close"><X size={32} /></button>
          <div className="max-w-3xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={zoom.image_url} alt={zoom.caption || ""} className="max-w-full max-h-[80vh] object-contain" />
            {zoom.caption && <p className="text-white/90 text-sm mt-3 text-center">{zoom.caption}</p>}
          </div>
        </div>
      )}
    </section>
  );
}
